import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { getLocalDateString, getPayPeriodInfo, addDays, getStickDates } from '../utils/dateUtils';

const ScheduleViolations = () => {
  const { user } = useAuth();
  const [allScheduleData, setAllScheduleData] = useState([]);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ mandatoryBreaks: false });
  const [error, setError] = useState('');

  // Env-driven limit for max available days per stick (defaults to 5)
  const STICK_MAX_AVAILABLE_DAYS = parseInt(process.env.REACT_APP_STICK_MAX_AVAILABLE_DAYS || '5', 10);

  // Fetch user settings to know if mandatory breaks are enabled
  const fetchSettings = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setSettings({ mandatoryBreaks: false });
    }
  }, []);

  // Fetch all user schedule requests
  const fetchAllScheduleData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.get('/api/schedule-requests');
      const requests = response.data;
      
      // Convert to normalized format
      const scheduleData = requests.map(request => {
        let requestDateString;
        if (request.request_date instanceof Date) {
          requestDateString = getLocalDateString(request.request_date);
        } else if (typeof request.request_date === 'string') {
          if (request.request_date.includes('T') || request.request_date.includes('Z')) {
            requestDateString = request.request_date.split('T')[0];
          } else {
            requestDateString = request.request_date;
          }
        } else {
          requestDateString = getLocalDateString(new Date(request.request_date));
        }

        const availabilityValue = request.is_available === 1 || request.is_available === true;

        return {
          date: requestDateString,
          is_available: availabilityValue,
          payPeriodInfo: getPayPeriodInfo(new Date(requestDateString))
        };
      });

      // Sort by date
      scheduleData.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setAllScheduleData(scheduleData);
      
      // Analyze violations
      analyzeViolations(scheduleData);
      
    } catch (error) {
      console.error('Failed to fetch schedule data:', error);
      setError('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check stick days violations
  const checkStickViolations = (scheduleData) => {
    const stickViolations = [];
    const processedSticks = new Set();

    scheduleData.forEach(day => {
      if (!day.is_available) return;

      const stickDates = getStickDates(day.date);
      const stickKey = stickDates[0]; // Use first date as stick identifier
      
      if (processedSticks.has(stickKey)) return;
      processedSticks.add(stickKey);

      // Count available days in this stick
      const availableDaysInStick = stickDates.filter(stickDate => {
        const dayData = scheduleData.find(d => d.date === stickDate);
        return dayData && dayData.is_available;
      });

      if (availableDaysInStick.length > STICK_MAX_AVAILABLE_DAYS) {
        const payPeriodInfo = getPayPeriodInfo(new Date(day.date));
        stickViolations.push({
          type: 'stick_days',
          severity: 'error',
          title: 'Stick Days Limit Exceeded',
          description: `Working ${availableDaysInStick.length}/${STICK_MAX_AVAILABLE_DAYS} days in stick ${payPeriodInfo.stickNumber}`,
          dates: availableDaysInStick,
          stickInfo: {
            payPeriod: payPeriodInfo.payPeriodNumber,
            stick: payPeriodInfo.stickNumber,
            stickDates: stickDates
          }
        });
      }
    });

    return stickViolations;
  };

  // Check consecutive days violations
  const checkConsecutiveViolations = (scheduleData) => {
    if (!settings.mandatoryBreaks) return [];

    const violations = [];
    const availableDates = scheduleData
      .filter(day => day.is_available)
      .map(day => day.date)
      .sort();

    let consecutiveSequences = [];
    let currentSequence = [];

    availableDates.forEach((date, index) => {
      if (currentSequence.length === 0) {
        currentSequence = [date];
      } else {
        const prevDate = new Date(currentSequence[currentSequence.length - 1]);
        const currentDate = new Date(date);
        const daysDiff = Math.floor((currentDate - prevDate) / (1000 * 60 * 60 * 24));

        if (daysDiff === 1) {
          currentSequence.push(date);
        } else {
          if (currentSequence.length > 1) {
            consecutiveSequences.push([...currentSequence]);
          }
          currentSequence = [date];
        }
      }

      // Handle last sequence
      if (index === availableDates.length - 1 && currentSequence.length > 1) {
        consecutiveSequences.push([...currentSequence]);
      }
    });

    // Check for violations (more than 5 consecutive days)
    consecutiveSequences.forEach(sequence => {
      if (sequence.length > 5) {
        violations.push({
          type: 'consecutive_days',
          severity: 'error',
          title: 'Consecutive Days Limit Exceeded',
          description: `Working ${sequence.length} consecutive days (max 5 allowed)`,
          dates: sequence,
          sequenceInfo: {
            startDate: sequence[0],
            endDate: sequence[sequence.length - 1],
            totalDays: sequence.length
          }
        });
      }
    });

    return violations;
  };

  // Analyze all violations
  const analyzeViolations = (scheduleData) => {
    const allViolations = [
      ...checkStickViolations(scheduleData),
      ...checkConsecutiveViolations(scheduleData)
    ];

    // Sort violations by date (earliest first)
    allViolations.sort((a, b) => {
      const aDate = new Date(a.dates[0]);
      const bDate = new Date(b.dates[0]);
      return aDate - bDate;
    });

    setViolations(allViolations);
  };

  useEffect(() => {
    if (user) {
      fetchSettings();
      fetchAllScheduleData();
    }
  }, [user, fetchSettings, fetchAllScheduleData]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateRange = (dates) => {
    if (dates.length === 1) {
      return formatDate(dates[0]);
    }
    return `${formatDate(dates[0])} - ${formatDate(dates[dates.length - 1])}`;
  };

  const getViolationIcon = (type) => {
    switch (type) {
      case 'stick_days':
        return '📅';
      case 'consecutive_days':
        return '🔗';
      default:
        return '⚠️';
    }
  };

  const getViolationColor = (severity) => {
    switch (severity) {
      case 'error':
        return '#dc2626'; // red-600
      case 'warning':
        return '#d97706'; // amber-600
      default:
        return '#6b7280'; // gray-500
    }
  };

  const isPastViolation = (dates) => {
    const today = new Date();
    const violationEndDate = new Date(dates[dates.length - 1]);
    return violationEndDate < today;
  };

  const isFutureViolation = (dates) => {
    const today = new Date();
    const violationStartDate = new Date(dates[0]);
    return violationStartDate > today;
  };

  const getCurrentViolation = (dates) => {
    const today = new Date();
    const todayStr = getLocalDateString(today);
    return dates.includes(todayStr);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container" style={{ height: '400px' }}>
          <div className="loading-spinner"></div>
          <p>Analyzing schedule violations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="alert alert-error">
          {error}
        </div>
      </div>
    );
  }

  const pastViolations = violations.filter(v => isPastViolation(v.dates));
  const currentViolations = violations.filter(v => getCurrentViolation(v.dates));
  const futureViolations = violations.filter(v => isFutureViolation(v.dates));

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Schedule Rule Violations</h1>
        <p className="page-subtitle">
          Analysis of all scheduling rule violations across your complete schedule history
        </p>
      </div>

      {/* Summary */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Violations Summary</h2>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Total Violations</label>
            <p className="mb-0 text-lg font-bold" style={{ color: violations.length > 0 ? '#dc2626' : '#059669' }}>
              {violations.length} {violations.length === 1 ? 'violation' : 'violations'}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Past</label>
            <p className="mb-0 text-lg font-bold" style={{ color: '#6b7280' }}>
              {pastViolations.length}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Current</label>
            <p className="mb-0 text-lg font-bold" style={{ color: currentViolations.length > 0 ? '#dc2626' : '#6b7280' }}>
              {currentViolations.length}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Future</label>
            <p className="mb-0 text-lg font-bold" style={{ color: futureViolations.length > 0 ? '#d97706' : '#6b7280' }}>
              {futureViolations.length}
            </p>
          </div>
        </div>

        {!settings.mandatoryBreaks && (
          <div className="alert alert-info" style={{ marginTop: '1rem' }}>
            <strong>Note:</strong> Consecutive days violations are not shown because mandatory breaks are currently disabled.
          </div>
        )}
      </div>

      {violations.length === 0 ? (
        <div className="card">
          <div className="text-center" style={{ padding: '3rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h3 style={{ color: '#059669', marginBottom: '0.5rem' }}>No Violations Found</h3>
            <p style={{ color: '#6b7280' }}>
              Your schedule complies with all current scheduling rules.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Current Violations */}
          {currentViolations.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title" style={{ color: '#dc2626' }}>
                  🚨 Current Violations ({currentViolations.length})
                </h2>
              </div>
              
              <div className="violations-list">
                {currentViolations.map((violation, index) => (
                  <div key={index} className="violation-item" style={{ 
                    border: `2px solid ${getViolationColor(violation.severity)}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    backgroundColor: '#fef2f2'
                  }}>
                    <div className="violation-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>
                        {getViolationIcon(violation.type)}
                      </span>
                      <h3 style={{ margin: 0, color: getViolationColor(violation.severity) }}>
                        {violation.title}
                      </h3>
                    </div>
                    
                    <p style={{ margin: '0.5rem 0', fontWeight: 'bold' }}>
                      {violation.description}
                    </p>
                    
                    <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                      <strong>Dates:</strong> {formatDateRange(violation.dates)}
                    </p>
                    
                    {violation.stickInfo && (
                      <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                        <strong>Pay Period:</strong> {violation.stickInfo.payPeriod}, 
                        <strong> Stick:</strong> {violation.stickInfo.stick}
                      </p>
                    )}
                    
                    {violation.sequenceInfo && (
                      <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                        <strong>Sequence:</strong> {violation.sequenceInfo.totalDays} consecutive days
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Future Violations */}
          {futureViolations.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title" style={{ color: '#d97706' }}>
                  ⚠️ Future Violations ({futureViolations.length})
                </h2>
              </div>
              
              <div className="violations-list">
                {futureViolations.map((violation, index) => (
                  <div key={index} className="violation-item" style={{ 
                    border: `2px solid ${getViolationColor(violation.severity)}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    backgroundColor: '#fffbeb'
                  }}>
                    <div className="violation-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>
                        {getViolationIcon(violation.type)}
                      </span>
                      <h3 style={{ margin: 0, color: getViolationColor(violation.severity) }}>
                        {violation.title}
                      </h3>
                    </div>
                    
                    <p style={{ margin: '0.5rem 0', fontWeight: 'bold' }}>
                      {violation.description}
                    </p>
                    
                    <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                      <strong>Dates:</strong> {formatDateRange(violation.dates)}
                    </p>
                    
                    {violation.stickInfo && (
                      <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                        <strong>Pay Period:</strong> {violation.stickInfo.payPeriod}, 
                        <strong> Stick:</strong> {violation.stickInfo.stick}
                      </p>
                    )}
                    
                    {violation.sequenceInfo && (
                      <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                        <strong>Sequence:</strong> {violation.sequenceInfo.totalDays} consecutive days
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Past Violations */}
          {pastViolations.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title" style={{ color: '#6b7280' }}>
                  📋 Past Violations ({pastViolations.length})
                </h2>
              </div>
              
              <div className="violations-list">
                {pastViolations.map((violation, index) => (
                  <div key={index} className="violation-item" style={{ 
                    border: `2px solid #e5e7eb`,
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    backgroundColor: '#f9fafb'
                  }}>
                    <div className="violation-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>
                        {getViolationIcon(violation.type)}
                      </span>
                      <h3 style={{ margin: 0, color: '#6b7280' }}>
                        {violation.title}
                      </h3>
                    </div>
                    
                    <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                      {violation.description}
                    </p>
                    
                    <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                      <strong>Dates:</strong> {formatDateRange(violation.dates)}
                    </p>
                    
                    {violation.stickInfo && (
                      <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                        <strong>Pay Period:</strong> {violation.stickInfo.payPeriod}, 
                        <strong> Stick:</strong> {violation.stickInfo.stick}
                      </p>
                    )}
                    
                    {violation.sequenceInfo && (
                      <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                        <strong>Sequence:</strong> {violation.sequenceInfo.totalDays} consecutive days
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Rule Explanations */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Scheduling Rules</h2>
        </div>
        
        <div style={{ padding: '1rem 0' }}>
          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>
              📅 Stick Days Rule
            </h4>
            <p style={{ margin: 0, color: '#6b7280' }}>
              Maximum {STICK_MAX_AVAILABLE_DAYS} available days per stick (7-day period). Each pay period has 2 sticks.
            </p>
          </div>
          
          {settings.mandatoryBreaks && (
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>
                🔗 Consecutive Days Rule
              </h4>
              <p style={{ margin: 0, color: '#6b7280' }}>
                Maximum 5 consecutive available days. Mandatory break required after 5 days in a row.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleViolations; 